/**
 * Fluid API enrichment for SMART vaults (T2 / T3 / T4).
 *
 * WHY THIS EXISTS — the on-chain resolver cannot serve a smart vault.
 * `VaultResolver.getVaultsEntireData()` returns `supplyRateVault = 0` for every
 * smart-COLLATERAL vault and `borrowRateVault = 0` for every smart-DEBT vault
 * (verified live on all Ethereum smart vaults, 2026-08-14). Only the SIMPLE side
 * of a T2/T3 carries a rate on-chain. The smart side's economics live in the
 * Fluid DEX — a per-token liquidity leg plus a DEX trading yield — and neither
 * the vault resolver nor the liquidity resolver publishes the trading leg.
 *
 * Two things are therefore taken from `GET /v2/<chainId>/vaults`:
 *
 *  1. **Per-share token amounts** (`supplyDexData` / `borrowDexData`
 *     `token0PerShare` / `token1PerShare`). A smart side's `totalSupplyVault` /
 *     `totalBorrowVault` and every user position are denominated in **DEX LP
 *     shares**, so without these there is no token amount, no USD, and no
 *     health factor. This is load-bearing: a smart vault with no per-share data
 *     is skipped rather than reported with zeroes.
 *  2. **The DEX trading yield** (`supplyRate.dex.trading` /
 *     `borrowRate.dex.trading`).
 *
 * The per-token LIQUIDITY legs are NOT taken from here — we already read them
 * on-chain from `LiquidityResolver.getAllOverallTokensData()`, which is the
 * same source the API itself reports.
 *
 * TRAPS (both cost real debugging time; do not re-derive):
 *  - `tokenXPerShare` is scaled in **the token's own decimals**, not 1e18. Only
 *    the share count is 1e18. Treating it as 1e18 understates any leg whose
 *    token has 6 decimals by 1e12.
 *  - The API returns a **zeroed stub** (`id: '0'`, `address: 0x0…0`) for a side
 *    that has no DEX rather than omitting the key, so a truthiness check on
 *    `supplyDexData` is NOT a smart-side test. Use `isRealDex`.
 */

const FLUID_API_BASE = 'https://api.fluid.instadapp.io/v2'

/** Chains the Fluid API answers for. Others return `{"message":"Chain N is not supported"}`. */
const SUPPORTED_CHAINS = new Set(['1', '56', '137', '8453', '9745', '42161'])

/** Cache TTL — vault rates move with utilization, so keep this short. */
const TTL_MS = 60_000

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

/** Per-share amounts for one DEX side, in each token's OWN decimals per 1e18 shares. */
export interface FluidDexSideShares {
  dex: string
  /** Raw `token0PerShare` — token0 units (token0 decimals) per 1e18 shares. */
  token0PerShare: bigint
  /** Raw `token1PerShare` — token1 units (token1 decimals) per 1e18 shares. */
  token1PerShare: bigint
  /**
   * Decimals of each leg token, from the API's own token metadata.
   *
   * These CANNOT be taken from the vault's `supplyDecimals` / `borrowDecimals`:
   * on a smart side those describe the 18-decimal DEX SHARE, not the tokens.
   * Nor can they safely come from our token list, which may not carry a leg.
   */
  token0Decimals: number
  token1Decimals: number
}

/**
 * The subset of the API's per-vault payload we depend on.
 *
 * `supplyDexTradingRate` / `borrowDexTradingRate` are the Fluid 1e2 scale
 * (`552` = 5.52 %), matching the rest of the Fluid integration.
 */
export interface FluidApiVaultEnrichment {
  vaultId: number
  vaultType: number
  supplyShares?: FluidDexSideShares
  borrowShares?: FluidDexSideShares
  /**
   * DEX trading yield EARNED by the collateral LP position — ADDS to the
   * supply rate. Equals the `/dexes` route's `apy.supplyApy` for the
   * collateral DEX (verified across every Ethereum smart vault).
   */
  supplyDexTradingRate: number
  /**
   * DEX trading yield EARNED by the debt LP position. Fluid's "smart debt"
   * product is precisely that the debt doubles as DEX liquidity, so this
   * OFFSETS the borrow cost — see `SMART_DEBT_TRADING_SIGN` in the parser.
   * Equals the `/dexes` route's `apy.borrowApy` for the debt DEX.
   */
  borrowDexTradingRate: number
}

export type FluidApiChainData = {
  [vaultId: number]: FluidApiVaultEnrichment
}

type CacheEntry = {
  at: number
  data: FluidApiChainData | undefined
}

const cache: { [chainId: string]: CacheEntry | undefined } = {}
const inflight = new Map<string, Promise<FluidApiChainData | undefined>>()

/** A side has a real DEX only when the API did not hand back its zeroed stub. */
const isRealDex = (d: any): boolean =>
  !!d &&
  String(d.id ?? '0') !== '0' &&
  String(d.address ?? ZERO_ADDRESS).toLowerCase() !== ZERO_ADDRESS

const toBigInt = (v: any): bigint => {
  try {
    return BigInt(v ?? 0)
  } catch {
    return 0n
  }
}

const parseSide = (d: any, sideToken: any): FluidDexSideShares | undefined => {
  if (!isRealDex(d)) return undefined
  const token0PerShare = toBigInt(d.token0PerShare)
  const token1PerShare = toBigInt(d.token1PerShare)
  // Both zero means the side has no outstanding shares — there is nothing to
  // convert and any position on it would price at 0. Treat as unusable.
  if (token0PerShare === 0n && token1PerShare === 0n) return undefined
  const d0 = Number(sideToken?.token0?.decimals)
  const d1 = Number(sideToken?.token1?.decimals)
  if (!Number.isFinite(d0) || !Number.isFinite(d1)) return undefined
  return {
    dex: String(d.address).toLowerCase(),
    token0PerShare,
    token1PerShare,
    token0Decimals: d0,
    token1Decimals: d1,
  }
}

const parseChainPayload = (raw: any[]): FluidApiChainData => {
  const out: FluidApiChainData = {}
  for (const v of raw ?? []) {
    const vaultId = Number(v?.id ?? NaN)
    if (!Number.isFinite(vaultId)) continue
    // API `type` is 1..4; the on-chain `vaultType` is 10000..40000.
    const vaultType = Number(v?.type ?? 0)
    out[vaultId] = {
      vaultId,
      vaultType,
      supplyShares: parseSide(v?.supplyDexData, v?.supplyToken),
      borrowShares: parseSide(v?.borrowDexData, v?.borrowToken),
      supplyDexTradingRate: Number(v?.supplyRate?.dex?.trading ?? 0),
      borrowDexTradingRate: Number(v?.borrowRate?.dex?.trading ?? 0),
    }
  }
  return out
}

/**
 * Fetch (and cache) the Fluid API enrichment for a chain.
 *
 * Returns `undefined` when the chain is unsupported or the fetch failed —
 * callers must then SKIP smart vaults rather than emit them with zeroed
 * amounts and rates. T1 vaults are unaffected and keep working purely
 * on-chain.
 */
export const fetchFluidApiData = async (
  chainId: string,
): Promise<FluidApiChainData | undefined> => {
  if (!SUPPORTED_CHAINS.has(chainId)) return undefined

  const hit = cache[chainId]
  if (hit && Date.now() - hit.at < TTL_MS) return hit.data
  const pending = inflight.get(chainId)
  if (pending) return pending

  const run = (async () => {
    try {
      const res = await fetch(`${FLUID_API_BASE}/${chainId}/vaults`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      if (!Array.isArray(json)) throw new Error('unexpected payload')
      const data = parseChainPayload(json)
      cache[chainId] = { at: Date.now(), data }
      return data
    } catch {
      // Cache the miss briefly so a hard outage does not produce one request
      // per vault-data refresh, but keep any previously good data usable.
      const prev = cache[chainId]?.data
      cache[chainId] = { at: Date.now(), data: prev }
      return prev
    } finally {
      inflight.delete(chainId)
    }
  })()

  inflight.set(chainId, run)
  return run
}

/** Synchronous read of whatever `fetchFluidApiData` last cached for a chain. */
export const getCachedFluidApiData = (
  chainId: string,
): FluidApiChainData | undefined => cache[chainId]?.data

/** Test seam — drop cached API state. */
export const __resetFluidApiCache = () => {
  for (const k of Object.keys(cache)) delete cache[k]
}

/** Test seam — inject API state without a network round-trip. */
export const __seedFluidApiCache = (
  chainId: string,
  data: FluidApiChainData,
) => {
  cache[chainId] = { at: Date.now(), data }
}
